import { useState, useEffect } from 'react';
import { useRoute, Link } from 'wouter';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '@/store/store';
import useCurrentAccount from '@/hooks/useCurrentAccount';
import DottedSpinner from "@/components/DottedSpinner";
import AccessFeatureLockedView from "@/components/AccessFeatureLockedView";
import AccessFeatureUnlockedView from "@/components/AccessFeatureUnlockedView";
import SessionStorage from '@/functions/SessionStorage';

const ACCESS_KEYS_STORAGE = SessionStorage.buildKey('productAccessKeys');

export const getAccessKeys = () => {
  const stored = SessionStorage.getItem(ACCESS_KEYS_STORAGE);
  return stored ? stored : {};
};

export const saveAccessKey = (accessFeatureId, accessKey) => {
  const keys = getAccessKeys();
  keys[accessFeatureId] = accessKey;
  SessionStorage.setItem(ACCESS_KEYS_STORAGE, keys);
};

const AccessFeature = () => {
  const dispatch = useDispatch();
  const [, params] = useRoute('/product/:id');
  const accessFeatureId = params.id;
  const [accessKeyInput, setAccessKeyInput] = useState(getAccessKeys()[accessFeatureId] || '');
  const [hasProductAccess, setHasProductAccess] = useState(false);
  const [accessKeyInputError, setAccessKeyInputError] = useState('');
  const { currentAccount, currentAccountLoading } = useCurrentAccount();
  const accessFeature = useSelector(({ accessFeatures }) => {
    const afId = parseInt(accessFeatureId) || accessFeatureId;
    return Object.values(accessFeatures.records).find((af) => [af.id, af.identity].includes(afId));
  });
  const accessFeatureLoading = useSelector(({ accessFeatures }) => accessFeatures.loading);
  const accessKey = useSelector(({ accessKeys }) => (
    Object.values(accessKeys.records).find((ak) => (
      ak.account_id === currentAccount?.id || ak.access_feature_id === accessFeature?.id
    ))
  ));

  useEffect(() => {
    dispatch(actions.accessFeatures.loadOne({ id: accessFeatureId }));
    dispatch(actions.accessKeys.loadAll());
  }, []);

  useEffect(() => {
    if (accessKey) {
      const load = async () =>
        await dispatch(actions.accessFeatures.display({ id: accessFeatureId, code: accessKey.code }));

      load().then(() => { setHasProductAccess(true); });
    }
  }, [accessKey]);

  if (!accessFeature && !accessFeatureLoading) {
    return (
      <div className="container" style={{ maxWidth: '1080px' }}>
        <div className="py-5 text-center">
          <h1>Product Not Found</h1>
          <p className="text-muted mb-4">The product you're looking for doesn't exist.</p>
          <Link to="/" className="btn btn-primary">Back to Products</Link>
        </div>
      </div>
    );
  }

  const handleAccessKeySubmit = async (e) => {
    e.preventDefault();
    const code = accessKeyInput.trim();
    const response = await dispatch(actions.accessFeatures.display({ id: accessFeatureId, code }));
    if (response.error) {
      setAccessKeyInputError(`Invalid access key. ${response.error.message}`);
    } else {
      dispatch(actions.accessKeys.loadOne({ code }));
      saveAccessKey(accessFeatureId, code);
      setHasProductAccess(true);
    }
  };

  const loading = accessFeatureLoading || currentAccountLoading;

  return (
    <div className="container">
      <div>
        <Link to="" className="btn btn-link ps-0 mb-3">
          <i className="fas fa-arrow-left me-2"></i>
          Back to Products
        </Link>

        {loading ? (
          <DottedSpinner />
        ) : (
          <>
            {!hasProductAccess ? (
              <AccessFeatureLockedView
                accessFeature={accessFeature}
                accessKeyInput={accessKeyInput}
                setAccessKeyInput={setAccessKeyInput}
                accessKeyInputError={accessKeyInputError}
                onSubmit={handleAccessKeySubmit}
              />
            ) : (
              <AccessFeatureUnlockedView currentAccount={currentAccount} accessFeature={accessFeature} accessKey={accessKey} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AccessFeature;